import { useNavigate } from 'react-router-dom'
import logo from '../../assets/logo.svg'
import './StaticPage.css'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'

export function Help() {
  const navigate = useNavigate()

  return (
    <div className="static-page">
      <header className="static-header">
        <div className="static-header-content">
          <img 
            src={logo} 
            alt="Ordelio Logo" 
            className="static-logo clickable" 
            onClick={() => navigate('/')}
          />
          <button onClick={() => navigate('/')} className="back-to-home-btn">
            ← Back to Home
          </button>
        </div>
      </header>

      <PwaUpdateBanner />

      <main className="static-content">
        <article className="content-container">
          <h1>Help & Documentation</h1>
          
          <section className="content-section">
            <h2>Getting Started</h2>
            
            <h3>1. Sign In</h3>
            <p>
              Click "Get Started with Google" on the homepage to sign in using your Google account. 
              This allows you to access your lists from any device and collaborate with others.
            </p>

            <h3>2. Create a Scenario</h3>
            <p>
              A Scenario is your organizational container for a repeatable routine (e.g.,
              "Weekly Groceries", "Hardware Store", or "Travel Packing"). To create one:
            </p>
            <ul>
              <li>Click "Create New Scenario" on the dashboard</li>
              <li>Enter a name and optional description</li>
              <li>Click "Create"</li>
            </ul>

            <h3>3. Create a Shopping List</h3>
            <p>
              Inside your scenario, click "+ New List" to create a shopping list for a specific 
              trip (or checklist). Give it a descriptive name like "Monday Shop - Feb 4" or "Weekend Trip".
            </p>

            <h3>4. Add Items</h3>
            <p>
              Start typing item names in the input box. As you type, you'll see suggestions based 
              on items you've added before. Press Enter or click "+" to add the item.
            </p>
          </section>

          <section className="content-section">
            <h2>Key Features</h2>

            <h3>Offline Mode</h3>
            <p>
              Ordelio works perfectly without internet. All changes are saved locally and sync 
              automatically when you're back online. The sync indicator in the top-right shows 
              your connection status.
            </p>

            <h3>Collaboration (Requires Internet)</h3>
            <p>
              To share a scenario with family or friends, you'll need an internet connection:
            </p>
            <ul>
              <li>Open the scenario</li>
              <li>Click the menu (⋮) in the top-right</li>
              <li>Select "Share"</li>
              <li>Enter their email address and choose their access level</li>
              <li>Click "Send Invite"</li>
            </ul>
            <p>
              When connected to the internet, collaborators see updates when items are added or marked complete.
            </p>

            <h3>Flows (Store Routes)</h3>
            <p>
              Flows organize your items in the order you want to follow (most commonly: the order you encounter them in a store):
            </p>
            <ul>
              <li>Navigate to the "Flows" section in your scenario</li>
              <li>Click "+ New Flow" and name it (e.g., "Aldi Route")</li>
              <li>Drag items into your preferred shopping order</li>
              <li>Apply the flow to any list for optimized shopping</li>
            </ul>

            <h3>Item Suggestions</h3>
            <p>
              Ordelio remembers your shopping history. When you start typing, you'll see 
              suggestions for items you've used before, making list building faster.
            </p>
          </section>

          <section className="content-section">
            <h2>Frequently Asked Questions</h2>

            <h3>Can I use Ordelio for non-shopping checklists (like travel packing)?</h3>
            <p>
              Yes. Scenarios and lists work well for any repeatable checklist. Many people keep a travel packing list,
              a weekly errands list, or a "restock" list that they reuse.
            </p>

            <h3>Can I use Ordelio without internet?</h3>
            <p>
              Yes! Ordelio is designed to work offline. All features function without internet, 
              and your changes sync when you're back online.
            </p>

            <h3>How many people can collaborate on a scenario?</h3>
            <p>
              There's no limit. You can invite as many collaborators as you need.
            </p>

            <h3>Can I use Ordelio on multiple devices?</h3>
            <p>
              Absolutely! Sign in with the same Google account on any device, and your scenarios 
              will sync automatically.
            </p>

            <h3>What happens if two people edit the same item?</h3>
            <p>
              Ordelio handles conflicts automatically using a last-write-wins approach. Everyone 
              eventually sees the same final state.
            </p>

            <h3>Do deleted items disappear from suggestions?</h3>
            <p>
              No. Items remain in your scenario's item collection even when removed from specific 
              lists, so they'll still appear as suggestions for future lists.
            </p>

            <h3>How do I leave a shared scenario?</h3>
            <p>
              Open the scenario, click the menu, and select "Leave Scenario." The scenario will 
              be removed from your dashboard but remain accessible to other collaborators.
            </p>

            <h3>Is there a mobile app?</h3>
            <p>
              Ordelio is a web application that works great on mobile browsers. You can add it 
              to your phone's home screen for an app-like experience.
            </p>
          </section>

          <section className="content-section">
            <h2>Troubleshooting</h2>

            <h3>Changes aren't syncing</h3>
            <p>
              First, check your internet connection. If you're online but changes aren't syncing:
            </p>
            <ul>
              <li>Try refreshing your browser</li>
              <li>Check the sync status indicator in the top-right</li>
              <li>Sign out and sign back in</li>
            </ul>

            <h3>I don't see a collaborator's changes</h3>
            <p>
              This usually means they're offline or there's a brief sync delay. Wait 10-20 seconds 
              and refresh your browser. Make sure both of you have internet connection.
            </p>

            <h3>The app is slow</h3>
            <p>
              Try these solutions:
            </p>
            <ul>
              <li>Close unnecessary browser tabs</li>
              <li>Clear your browser cache</li>
              <li>Restart your browser</li>
              <li>Check your internet speed</li>
            </ul>

            <h3>I accidentally deleted something</h3>
            <p>
              Deleted lists cannot be recovered. However, items deleted from a list still appear 
              in suggestions, so you can easily re-add them to a new list.
            </p>
          </section>

          <section className="content-section">
            <h2>Tips & Best Practices</h2>
            <ul>
              <li><strong>Keep scenarios focused:</strong> Create separate scenarios for different shopping categories</li>
              <li><strong>Use descriptive names:</strong> Name lists with dates or purposes (e.g., "Party Prep - Feb 10")</li>
              <li><strong>Create flows early:</strong> Set up store routes when you start using a scenario</li>
              <li><strong>Be consistent:</strong> Use suggestions to maintain consistent item naming</li>
              <li><strong>Add quantities:</strong> Include quantities in item names when helpful (e.g., "Apples (5)")</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Contact Support</h2>
            <p>
              If you encounter issues or have questions not covered here, please contact us:
            </p>
            <p><strong>Email:</strong> support@ordelio.com</p>
          </section>

          <div className="cta-container">
            <button onClick={() => navigate('/')} className="cta-button">
              Start Using Ordelio
            </button>
          </div>
        </article>
      </main>

      <footer className="static-footer">
        <p>© 2026 Ordelio. All rights reserved.</p>
      </footer>
    </div>
  )
}
